export class StarField extends HTMLElement {
    constructor() {
        super();
        this.canvas = null;
        this.ctx = null;
        this.layers = [];
        this.animationFrameId = null;
        this.lastScrollY = 0;
        this.scrollDelta = 0;
    }

    connectedCallback() {
        this.createCanvas();
        this.setupViewportResize();
        this.setupScrollTracking();
    }

    createCanvas() {
        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d');
        this.appendChild(this.canvas);
    }

    setupViewportResize() {
        // Use viewport dimensions for the canvas
        const updateSize = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            if (width > 0 && height > 0) {
                this.handleResize(width, height);
            }
        };

        // Initial size
        updateSize();

        // Listen for viewport resize
        window.addEventListener('resize', updateSize);
        this.resizeHandler = updateSize;
    }

    setupScrollTracking() {
        this.lastScrollY = window.scrollY;

        const onScroll = () => {
            const currentScrollY = window.scrollY;
            this.scrollDelta = currentScrollY - this.lastScrollY;
            this.lastScrollY = currentScrollY;
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        this.scrollHandler = onScroll;
    }

    handleResize(width, height) {
        this.canvas.width = width;
        this.canvas.height = height;

        if (this.layers.length === 0) {
            this.generateLayers();
            this.animate();
        } else {
            this.redistributeStars(width, height);
        }
    }

    generateLayers() {
        const width = this.canvas.width;
        const height = this.canvas.height;

        // 3 depth layers with different properties (500 total stars)
        this.layers = [
            { stars: [], speed: 0.02, count: 350 },  // Far layer - most stars, smallest, slowest
            { stars: [], speed: 0.05, count: 110 },  // Mid layer
            { stars: [], speed: 0.1, count: 40 }     // Near layer - fewer, bigger, faster
        ];

        // Generate star clusters (Milky Way effect)
        const clusters = this.generateClusters(3, width, height);

        this.layers.forEach((layer, layerIndex) => {
            for (let i = 0; i < layer.count; i++) {
                const star = this.createStar(width, height, layerIndex, clusters);
                layer.stars.push(star);
            }
        });
    }

    generateClusters(count, width, height) {
        const clusters = [];
        for (let i = 0; i < count; i++) {
            clusters.push({
                x: Math.random() * width,
                y: Math.random() * height,
                radius: Math.random() * 150 + 100,
                density: Math.random() * 0.3 + 0.2
            });
        }
        return clusters;
    }

    createStar(width, height, layerIndex, clusters) {
        // Determine position - bias towards clusters
        let x, y;
        const useCluster = Math.random() < 0.3 && clusters.length > 0;

        if (useCluster) {
            const cluster = clusters[Math.floor(Math.random() * clusters.length)];
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * cluster.radius;
            x = cluster.x + Math.cos(angle) * distance;
            y = cluster.y + Math.sin(angle) * distance;
            // Wrap around edges
            x = ((x % width) + width) % width;
            y = ((y % height) + height) % height;
        } else {
            x = Math.random() * width;
            y = Math.random() * height;
        }

        // Size based on layer - most stars are tiny points
        const sizeRoll = Math.random();
        let size;
        if (layerIndex === 0) {
            // Far layer: almost all are tiny points
            size = sizeRoll < 0.9 ? 0.5 : (sizeRoll < 0.98 ? 0.75 : 1);
        } else if (layerIndex === 1) {
            // Mid layer
            size = sizeRoll < 0.8 ? 0.5 : (sizeRoll < 0.95 ? 0.75 : 1.25);
        } else {
            // Near layer: slightly larger but still small
            size = sizeRoll < 0.6 ? 0.75 : (sizeRoll < 0.9 ? 1 : 1.5);
        }

        // Brightness distribution - balanced mix
        const brightnessRoll = Math.random();
        let brightness;
        if (brightnessRoll < 0.5) {
            brightness = Math.random() * 0.3 + 0.3;   // Dim (0.3 - 0.6)
        } else if (brightnessRoll < 0.85) {
            brightness = Math.random() * 0.25 + 0.6;  // Medium (0.6 - 0.85)
        } else {
            brightness = Math.random() * 0.15 + 0.85; // Bright (0.85 - 1.0)
        }

        // Realistic star colors based on stellar classification (enhanced saturation)
        // Real stars range from blue (hot) through white to orange/red (cool)
        const tempRoll = Math.random();
        let color;
        if (tempRoll < 0.10) {
            // O/B class - Vivid blue-white (rare, hot stars)
            color = { r: 130 + Math.random() * 40, g: 160 + Math.random() * 40, b: 255 };
        } else if (tempRoll < 0.22) {
            // A class - Ice blue
            color = { r: 180 + Math.random() * 30, g: 200 + Math.random() * 30, b: 255 };
        } else if (tempRoll < 0.40) {
            // F class - Warm white
            color = { r: 255, g: 250 + Math.random() * 5, b: 240 + Math.random() * 15 };
        } else if (tempRoll < 0.60) {
            // G class - Golden yellow (sun-like)
            color = { r: 255, g: 235 + Math.random() * 15, b: 180 + Math.random() * 40 };
        } else if (tempRoll < 0.80) {
            // K class - Warm orange
            color = { r: 255, g: 200 + Math.random() * 30, b: 140 + Math.random() * 40 };
        } else {
            // M class - Deep red-orange
            color = { r: 255, g: 160 + Math.random() * 40, b: 100 + Math.random() * 50 };
        }

        // Twinkling: atmospheric scintillation effect
        const doesTwinkle = true;

        // Subtle glow for brighter stars
        const hasGlow = brightness > 0.65 && size >= 1;
        const glowRadius = hasGlow ? size * 3 : 0;

        // Create star with initial twinkle cycle
        const star = {
            x,
            y,
            size,
            brightness,
            baseBrightness: brightness,
            color,
            doesTwinkle,
            hasGlow,
            glowRadius,
            baseX: x,
            baseY: y,
            // Twinkle state tracking
            cycleStartTime: -Math.random() * 10, // Stagger initial start times
            currentCycle: null,
            // Fade-and-move tracking
            cyclesCompleted: 0,
            cyclesUntilMove: Math.floor(Math.random() * 8) + 4, // Move after 4-11 cycles
            isRepositioning: false,
            repositionPhase: 0, // 0 = normal, 1 = fading out, 2 = fading in
            repositionProgress: 0,
            targetX: x,
            targetY: y
        };

        // Initialize first twinkle cycle
        if (doesTwinkle) {
            this.randomizeTwinkleCycle(star);
        }

        return star;
    }

    randomizeTwinkleCycle(star) {
        star.currentCycle = {
            // Cycle duration: 1 to 7 seconds
            duration: Math.random() * 6 + 1,
            // Time ratios for fade in/out (0.15 to 0.45 of cycle each)
            fadeOutRatio: Math.random() * 0.3 + 0.15,
            fadeInRatio: Math.random() * 0.3 + 0.15,
            // Hold times at min and max (0 to 2.5 seconds each)
            holdAtMin: Math.random() * 2.5,
            holdAtMax: Math.random() * 2.5,
            // Brightness range for this cycle
            minBrightness: Math.random() * 0.25 + 0.35,  // 0.35 to 0.6
            maxBrightness: Math.random() * 0.2 + 0.8     // 0.8 to 1.0
        };
    }

    startRepositioning(star, width, height) {
        star.isRepositioning = true;
        star.repositionPhase = 1; // Start fading out
        star.repositionProgress = 0;
        star.repositionDuration = Math.random() * 1.5 + 1; // 1-2.5 seconds to fade out/in

        // Pick new random position
        star.targetX = Math.random() * width;
        star.targetY = Math.random() * height;

        // Reset cycle counter with new random target
        star.cyclesCompleted = 0;
        star.cyclesUntilMove = Math.floor(Math.random() * 8) + 4;
    }

    updateRepositioning(star, deltaTime, width, height) {
        if (!star.isRepositioning) return 1.0; // Return full brightness multiplier

        star.repositionProgress += deltaTime / star.repositionDuration;

        if (star.repositionPhase === 1) {
            // Fading out
            if (star.repositionProgress >= 1) {
                // Finished fading out - move to new position
                star.baseX = star.targetX;
                star.baseY = star.targetY;
                star.x = star.targetX;
                star.y = star.targetY;
                star.repositionPhase = 2; // Start fading in
                star.repositionProgress = 0;

                // Optionally give star new visual properties
                if (Math.random() < 0.3) {
                    // 30% chance to change color temperature slightly
                    const shift = (Math.random() - 0.5) * 20;
                    star.color.r = Math.min(255, Math.max(100, star.color.r + shift));
                    star.color.g = Math.min(255, Math.max(100, star.color.g + shift));
                }
            }
            // Return fade-out multiplier (1 -> 0)
            return Math.max(0, 1 - star.repositionProgress);
        } else if (star.repositionPhase === 2) {
            // Fading in
            if (star.repositionProgress >= 1) {
                // Finished fading in - back to normal
                star.isRepositioning = false;
                star.repositionPhase = 0;
                return 1.0;
            }
            // Return fade-in multiplier (0 -> 1)
            return Math.min(1, star.repositionProgress);
        }

        return 1.0;
    }

    redistributeStars(width, height) {
        const clusters = this.generateClusters(3, width, height);
        this.layers.forEach((layer, layerIndex) => {
            layer.stars.forEach(star => {
                const newStar = this.createStar(width, height, layerIndex, clusters);
                Object.assign(star, newStar);
            });
        });
    }

    drawStar(star, x) {
        const ctx = this.ctx;
        const { r, g, b } = star.color;

        // Draw soft glow for bright stars
        if (star.hasGlow) {
            const gradient = ctx.createRadialGradient(
                x, star.y, 0,
                x, star.y, star.glowRadius
            );
            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${star.brightness * 0.4})`);
            gradient.addColorStop(0.4, `rgba(${r}, ${g}, ${b}, ${star.brightness * 0.15})`);
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.beginPath();
            ctx.arc(x, star.y, star.glowRadius, 0, Math.PI * 2);
            ctx.fillStyle = gradient;
            ctx.fill();
        }

        // Draw the star core as a small point with soft edges
        if (star.size <= 1) {
            // Tiny stars: just a pixel with the right color/brightness
            ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${star.brightness})`;
            ctx.fillRect(x - 0.5, star.y - 0.5, 1, 1);
        } else {
            // Larger stars: soft radial gradient for natural look
            const coreGradient = ctx.createRadialGradient(
                x, star.y, 0,
                x, star.y, star.size
            );
            coreGradient.addColorStop(0, `rgba(255, 255, 255, ${star.brightness})`);
            coreGradient.addColorStop(0.3, `rgba(${r}, ${g}, ${b}, ${star.brightness * 0.9})`);
            coreGradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

            ctx.beginPath();
            ctx.arc(x, star.y, star.size, 0, Math.PI * 2);
            ctx.fillStyle = coreGradient;
            ctx.fill();
        }
    }

    getTwinkleBrightness(star, time, width, height) {
        const cycle = star.currentCycle;

        // Calculate total cycle duration including holds
        const totalCycleDuration = cycle.duration + cycle.holdAtMin + cycle.holdAtMax;

        // Time elapsed since this cycle started
        const elapsed = time - star.cycleStartTime;

        // Check if we've completed this cycle and need a new one
        if (elapsed >= totalCycleDuration) {
            // Increment cycle counter
            star.cyclesCompleted++;

            // Check if it's time to reposition
            if (star.cyclesCompleted >= star.cyclesUntilMove && !star.isRepositioning) {
                this.startRepositioning(star, width, height);
            }

            // Start a new cycle with new random parameters
            star.cycleStartTime = time;
            this.randomizeTwinkleCycle(star);
            return this.getTwinkleBrightness(star, time, width, height); // Recalculate with new cycle
        }

        // Calculate phase durations
        const fadeOutDuration = cycle.duration * cycle.fadeOutRatio;
        const fadeInDuration = cycle.duration * cycle.fadeInRatio;
        const transitionDuration = cycle.duration - fadeOutDuration - fadeInDuration;
        const halfTransition = Math.max(transitionDuration / 2, 0.1);

        // Phase boundaries
        const fadeOutEnd = fadeOutDuration;
        const holdMinEnd = fadeOutEnd + cycle.holdAtMin;
        const fadeInStart = holdMinEnd + halfTransition;
        const fadeInEnd = fadeInStart + fadeInDuration;
        const holdMaxEnd = fadeInEnd + cycle.holdAtMax;

        // Brightness range for this cycle
        const minB = cycle.minBrightness;
        const maxB = cycle.maxBrightness;
        const midB = (minB + maxB) / 2;

        let brightness;
        if (elapsed < fadeOutEnd) {
            // Fading out from max to min
            const t = elapsed / fadeOutDuration;
            brightness = maxB - t * (maxB - minB);
        } else if (elapsed < holdMinEnd) {
            // Holding at minimum
            brightness = minB;
        } else if (elapsed < fadeInStart) {
            // Transitioning from min toward mid
            const t = (elapsed - holdMinEnd) / halfTransition;
            brightness = minB + t * (midB - minB);
        } else if (elapsed < fadeInEnd) {
            // Fading in from mid to max
            const t = (elapsed - fadeInStart) / fadeInDuration;
            brightness = midB + t * (maxB - midB);
        } else if (elapsed < holdMaxEnd) {
            // Holding at maximum
            brightness = maxB;
        } else {
            // Transitioning from max back toward fade out start
            const t = (elapsed - holdMaxEnd) / halfTransition;
            brightness = maxB - t * (maxB - midB);
        }

        return star.baseBrightness * brightness;
    }

    animate() {
        const time = performance.now() * 0.001;
        const width = this.canvas.width;
        const height = this.canvas.height;

        // Calculate delta time for smooth animations
        const deltaTime = this.lastTime ? time - this.lastTime : 0.016;
        this.lastTime = time;

        // Get scroll delta and reset it
        const scrollDelta = this.scrollDelta;
        this.scrollDelta = 0;

        this.ctx.clearRect(0, 0, width, height);

        this.layers.forEach((layer, layerIndex) => {
            // Different parallax rates for each layer (far moves less, near moves more)
            const parallaxFactor = 0.3 + (layerIndex * 0.25); // 0.3, 0.55, 0.8

            layer.stars.forEach(star => {
                // Move star based on scroll (parallax effect)
                if (scrollDelta !== 0) {
                    star.y -= scrollDelta * parallaxFactor;
                    star.baseY = star.y;

                    // Recycle stars that scroll out of view
                    if (star.y < -20) {
                        // Star scrolled off top - move to bottom
                        star.y = height + 20 + Math.random() * 50;
                        star.baseY = star.y;
                        star.x = Math.random() * width;
                        star.baseX = star.x;
                    } else if (star.y > height + 20) {
                        // Star scrolled off bottom - move to top
                        star.y = -20 - Math.random() * 50;
                        star.baseY = star.y;
                        star.x = Math.random() * width;
                        star.baseX = star.x;
                    }
                }

                // Update twinkling with varied timing
                if (star.doesTwinkle) {
                    star.brightness = this.getTwinkleBrightness(star, time, width, height);
                }

                // Handle repositioning fade effect
                const repositionMultiplier = this.updateRepositioning(star, deltaTime, width, height);
                const displayBrightness = star.brightness * repositionMultiplier;

                // Subtle horizontal drift
                const drift = time * layer.speed;
                const x = ((star.baseX + drift) % width + width) % width;

                // Only draw if visible (within viewport with some margin)
                if (displayBrightness > 0.01 && star.y > -20 && star.y < height + 20) {
                    const originalBrightness = star.brightness;
                    star.brightness = displayBrightness;
                    this.drawStar(star, x);
                    star.brightness = originalBrightness;
                }
            });
        });

        this.animationFrameId = requestAnimationFrame(() => this.animate());
    }

    disconnectedCallback() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
        }
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler);
        }
    }
}

customElements.define('star-field', StarField);
